import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'libs/i18n'
import { ConfirmModal, Select } from 'shared/components'
import { IOption } from 'interfaces/select.interface'
import { CheckedProduct } from 'features/products/types/products.types'

type PrintProductsDialogProps = {
	title: string
	products: CheckedProduct[]
	printVersion: IOption
	printOptions: IOption[]
	closeModal: () => void
	confirm: () => void
	onQuantityChange: (index: number, quantity: number) => void
	onVersionChange: (option: IOption) => void
}

const getQuantity = (product: CheckedProduct) => product.__print_quantity ?? 1

const stepButtonClassName =
	'grid h-full w-7 place-items-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:stroke-2'

export const PrintProductsDialog = ({
	title,
	products,
	printVersion,
	printOptions,
	closeModal,
	confirm,
	onQuantityChange,
	onVersionChange
}: PrintProductsDialogProps) => {
	const { t } = useTranslation()
	const description =
		products.length === 1
			? t('You are about to print a product. Are you sure you want to proceed?')
			: t('You are about to print {{ count }} products. Are you sure you want to proceed?', {
					count: products.length
				})

	return (
		<ConfirmModal
			isOpened
			title={title}
			description={description}
			closeModal={closeModal}
			confirm={confirm}>
			<div className="max-h-[320px] overflow-y-auto">
				<table className="w-full text-left text-[13px] text-foreground">
					<thead>
						<tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
							<th className="w-24 py-2 font-semibold">{t('In stock')}</th>
							<th className="py-2 font-semibold">{t('Product')}</th>
							<th className="w-20 py-2 text-right font-semibold">{t('Print')}</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-border">
						{products.map((product, index) => (
							<tr key={product.id}>
								<td className="py-2.5">
									<span className="inline-flex items-center whitespace-nowrap rounded-md border border-success/30 bg-success/10 px-2 py-0.5 text-[12px] font-semibold tabular-nums text-success">
										{t('{{ count }} in stock', { count: product.quantity })}
									</span>
								</td>
								<td className="break-words py-2.5 pr-3 font-medium">{product.name}</td>
								<td className="py-2.5 text-right">
									<span className="inline-flex h-8 items-center overflow-hidden rounded-md border border-border bg-card">
										<button
											type="button"
											aria-label={t('Decrease')}
											className={stepButtonClassName}
											onClick={() =>
												onQuantityChange(index, Math.max(1, getQuantity(product) - 1))
											}>
											<MinusIcon />
										</button>
										<input
											type="text"
											inputMode="numeric"
											aria-label={t('Print')}
											className="w-9 bg-transparent text-center text-[13px] font-semibold tabular-nums outline-none"
											value={getQuantity(product)}
											onChange={(e) => {
												const quantity = Number.parseInt(e.target.value)

												if (quantity >= 1) onQuantityChange(index, quantity)
											}}
										/>
										<button
											type="button"
											aria-label={t('Increase')}
											className={stepButtonClassName}
											onClick={() => onQuantityChange(index, getQuantity(product) + 1)}>
											<PlusIcon />
										</button>
									</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<Select
				id="printVersion"
				variant="form"
				className="mt-5"
				label={t('Label size')}
				options={printOptions}
				value={printVersion}
				isClearable={false}
				menuPortalTarget={document.body}
				menuPosition="fixed"
				onChange={onVersionChange}
			/>
		</ConfirmModal>
	)
}
